import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login, loginAsGuest } from "../services/authService";
import { useAuth } from "../services/AuthContext";
import Notification from "./Notification";
import EmailVerificationPanel from "./EmailVerificationPanel";
import { jwtDecode } from "jwt-decode";
import { MdArrowBack, MdEmail, MdLock } from "react-icons/md";
import LanguageSelector from "./LanguageSelector";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { hasAcceptedTerms, markTermsAccepted } from "../constants/termsAndConditions";
import {
  getLanguageCopy,
  getStoredLanguage,
  setStoredLanguage,
} from "../services/language";
import { t as i18nT } from "../i18n";
import "./LoginForm.css";


const LOGIN_TEXTS = {
  title: {
    es: "Inicia sesión en XIMA",
    en: "Log in to XIMA",
    ru: "Войти в XIMA",
    de: "Bei XIMA anmelden",
  },
  username: {
    es: "Correo electrónico",
    en: "Email address",
    ru: "Электронная почта",
    de: "E-Mail-Adresse",
  },
  usernamePlaceholder: {
    es: "Ingresa tu correo electrónico",
    en: "Enter your email address",
    ru: "Введите адрес электронной почты",
    de: "E-Mail-Adresse eingeben",
  },
  password: {
    es: "Contraseña",
    en: "Password *",
    ru: "Password *",
    de: "Passwort *",
  },
  passwordPlaceholder: {
    es: "Ingresa tu contraseña",
    en: "Enter your password",
    ru: "Enter your password",
    de: "Passwort eingeben",
  },
  rememberSession: {
    es: "Mantener sesión iniciada",
    en: "Keep me logged in",
    ru: "Keep me signed in",
    de: "Eingeloggt bleiben",
  },
  usernameRequired: {
    es: "El correo electrónico es obligatorio.",
    en: "Email is required.",
    ru: "Требуется электронная почта.",
    de: "E-Mail ist erforderlich.",
  },
  passwordRequired: {
    es: "La contraseña es obligatoria.",
    en: "Password is required.",
    ru: "Password is required.",
    de: "Passwort ist erforderlich.",
  },
  submit: {
    es: "Entrar a tu cuenta",
    en: "Log in to your account",
    ru: "Login to Your Account",
    de: "In dein Konto einloggen",
  },
  loading: {
    es: "Entrando...",
    en: "Logging in...",
    ru: "Logging in...",
    de: "Anmeldung...",
  },
  guestSubmit: {
    es: "Jugar sin registrarme",
    en: "Play without registering",
    ru: "Play without registering",
    de: "Ohne Registrierung spielen",
  },
  guestLoading: {
    es: "Entrando como invitado...",
    en: "Logging in as a guest",
    ru: "Entering as guest...",
    de: "Als Gast anmelden...",
  },
  noAccount: {
    es: "No tienes cuenta?",
    en: "Don't have an account?",
    ru: "Don't have an account?",
    de: "Noch kein Konto?",
  },
  register: {
    es: "Regístrate",
    en: "Sign up",
    ru: "Register",
    de: "Registrieren",
  },
  failedTitle: {
    es: "Error de acceso",
    en: "Login Failed",
    ru: "Login Failed",
    de: "Login fehlgeschlagen",
  },
  failedPending: {
    es: "Tu cuenta esta pendiente de aprobacion. Contacta a soporte.",
    en: "Your account is pending approval. Please contact support.",
    ru: "Your account is pending approval. Please contact support.",
    de: "Dein Konto wartet auf Freigabe. Bitte kontaktiere den Support.",
  },
  verificationRequired: {
    es: "Verifica tu correo para entrar.",
    en: "Verify your email to log in.",
    ru: "Verify your email to log in.",
    de: "Verifiziere deine E-Mail, um dich anzumelden.",
  },
  successTitle: {
    es: "Acceso correcto",
    en: "Login Successful",
    ru: "Login Successful",
    de: "Login erfolgreich",
  },
  successDescription: {
    es: "Bienvenido a tu panel!",
    en: "Welcome to your dashboard!",
    ru: "Welcome to your dashboard!",
    de: "Willkommen in deinem Dashboard!",
  },
  guestSuccessTitle: {
    es: "Acceso de invitado",
    en: "Guest Access",
    ru: "Guest Access",
    de: "Gastzugang",
  },
  guestSuccessDescription: {
    es: "Entraste como",
    en: "You entered as",
    ru: "You entered as",
    de: "Du bist angemeldet als",
  },
  formError: {
    es: "No se pudo iniciar sesion. Revisa tus credenciales.",
    en: "Login failed. Please check your credentials.",
    ru: "Login failed. Please check your credentials.",
    de: "Login fehlgeschlagen. Bitte pruefe deine Zugangsdaten.",
  },
  retryDescription: {
    es: "Revisa tus credenciales e intenta otra vez.",
    en: "Please check your credentials and try again.",
    ru: "Please check your credentials and try again.",
    de: "Bitte pruefe deine Zugangsdaten und versuche es erneut.",
  },
  termsGuestInline: {
    es: "Al continuar como invitado aceptas los Términos y Condiciones.",
    en: "By continuing as guest, you accept the Terms and Conditions.",
    ru: "Продолжая как гость, вы принимаете Условия использования.",
    de: "Als Gast akzeptierst du die Nutzungsbedingungen.",
  },
  continueWithEmail: {
    es: "Continuar con correo electrónico",
    en: "Continue with email",
    ru: "Продолжить с электронной почтой",
    de: "Mit E-Mail fortfahren",
  },
  emailOnlyDescription: {
    es: "Accede con el correo electrónico de tu cuenta XIMA.",
    en: "Use the email address connected to your XIMA account.",
    ru: "Используйте электронную почту вашей учетной записи XIMA.",
    de: "Verwende die E-Mail-Adresse deines XIMA-Kontos.",
  },
  back: {
    es: "Volver",
    en: "Back",
    ru: "Назад",
    de: "Zurück",
  },
};

const PREAUTH_TERMS_SUBJECT = "preauth";

const LoginForm = () => {
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [rememberSession, setRememberSession] = useState(true);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [verificationContact, setVerificationContact] = useState("");
  const [language, setLanguage] = useState(getStoredLanguage);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsModalMode, setTermsModalMode] = useState("guest");
  const [termsModalLoading, setTermsModalLoading] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, user } = useAuth();
  const t = useCallback(
    (key) => getLanguageCopy(language, LOGIN_TEXTS[key], key),
    [language]
  );
  const autoGuestTriggeredRef = useRef(false);
  const canUseGuestLogin = useMemo(() => {
    const normalizedRole = String(user?.role || "")
      .trim()
      .toLowerCase();
    return normalizedRole === "admin" || normalizedRole === "superuser";
  }, [user?.role]);

  const safeRedirectPath = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const redirectPath = String(params.get("redirect") || "").trim();
    if (!redirectPath) return "";
    if (!redirectPath.startsWith("/")) return "";
    if (redirectPath.startsWith("//")) return "";
    if (redirectPath.toLowerCase().startsWith("/login")) return "";
    return redirectPath;
  }, [location.search]);

  const shouldAutoGuest = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("autoguest") || "").trim() === "1";
  }, [location.search]);

  const resolvePostLoginPath = useCallback(
    (roleValue) => {
      const normalizedRole = String(roleValue || "")
        .trim()
        .toLowerCase();
      const canAccessGameRoutes =
        normalizedRole === "user" ||
        normalizedRole === "admin" ||
        normalizedRole === "superuser";

      if (safeRedirectPath) {
        const redirectRequiresGameRole = safeRedirectPath.startsWith("/game");
        if (!redirectRequiresGameRole || canAccessGameRoutes) {
          return safeRedirectPath;
        }
      }

      if (["member", "owner", "manager"].includes(normalizedRole)) {
        return "/owner/dashboard";
      }
      if (canAccessGameRoutes) {
        return "/game/lobby";
      }
      return "/login";
    },
    [safeRedirectPath]
  );

  useEffect(() => {
    setStoredLanguage(language);
  }, [language]);

  const onSubmit = async (event) => {
    event.preventDefault();
    const { identifier, password } = event.target.elements;
    const usernameValue = String(identifier?.value || "").trim();
    const passwordValue = String(password?.value || "");

    const formErrors = {};
    if (!usernameValue) formErrors.username = t("usernameRequired");
    if (!passwordValue) formErrors.password = t("passwordRequired");
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await login({
        identifier: usernameValue,
        password: passwordValue,
      });

      const token = response.data.token;
      const decodedToken = jwtDecode(token);
      const { role, state } = decodedToken;
      const normalizedRole = String(role || "")
        .trim()
        .toLowerCase();

      if (state !== "active") {
        setNotification({
          type: "error",
          message: t("failedTitle"),
          description: t("failedPending"),
        });
        setLoading(false);
        return;
      }

      authLogin(token, {
        persist: rememberSession,
      });
      setNotification({
        type: "success",
        message: t("successTitle"),
        description: t("successDescription"),
      });

      navigate(resolvePostLoginPath(normalizedRole));
    } catch (error) {
      if (error?.response?.data?.requiresEmailVerification) {
        setVerificationContact(error?.response?.data?.contact || usernameValue);
        setErrors({});
        setNotification({
          type: "warning",
          message: t("failedTitle"),
          description:
            error?.response?.data?.message ||
            t("verificationRequired"),
        });
        return;
      }

      const backendErrors = error?.response?.data?.errors;
      if (Array.isArray(backendErrors)) {
        const mappedErrors = backendErrors.reduce((accumulator, currentError) => {
          if (currentError?.path) {
            accumulator[currentError.path] = currentError.msg;
          }
          return accumulator;
        }, {});
        setErrors((previous) => ({ ...previous, ...mappedErrors }));
      } else {
        setErrors({ form: t("formError") });
      }

      setNotification({
        type: "error",
        message: t("failedTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("retryDescription"),
      });
    } finally {
      setLoading(false);
    }
  };

  const onGuestSubmit = useCallback(async () => {
    if (!canUseGuestLogin) return;
    if (loading || guestLoading) return;
    setErrors({});
    setGuestLoading(true);

    try {
      const response = await loginAsGuest();
      const token = String(response?.data?.token || "").trim();
      if (!token) {
        throw new Error("Guest token was not generated.");
      }

      authLogin(token, { persist: true });
      const guestName = String(response?.data?.guestName || "").trim();
      const guestDescription = guestName
        ? `${t("guestSuccessDescription")} ${guestName}.`
        : t("guestSuccessDescription");

      setNotification({
        type: "success",
        message: t("guestSuccessTitle"),
        description: guestDescription,
      });
      navigate(resolvePostLoginPath("user"));
    } catch (error) {
      setNotification({
        type: "error",
        message: t("failedTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("retryDescription"),
      });
    } finally {
      setGuestLoading(false);
    }
  }, [authLogin, canUseGuestLogin, guestLoading, loading, navigate, resolvePostLoginPath, t]);

  const openTermsModal = useCallback(
    async (mode) => {
      if (loading || guestLoading || termsModalLoading) return;
      const normalizedMode = mode === "register" ? "register" : "guest";
      if (normalizedMode === "guest" && !canUseGuestLogin) return;
      if (normalizedMode === "register" && hasAcceptedTerms(PREAUTH_TERMS_SUBJECT)) {
        if (normalizedMode === "register") {
          navigate("/register");
          return;
        }
      }
      setTermsModalMode(normalizedMode);
      setTermsModalOpen(true);
    },
    [canUseGuestLogin, guestLoading, loading, navigate, termsModalLoading]
  );

  const closeTermsModal = useCallback(() => {
    if (termsModalLoading) return;
    setTermsModalOpen(false);
  }, [termsModalLoading]);

  const onTermsAccept = useCallback(async () => {
    if (termsModalLoading) return;
    markTermsAccepted(PREAUTH_TERMS_SUBJECT);
    if (termsModalMode === "register") {
      setTermsModalOpen(false);
      navigate("/register");
      return;
    }
    if (!canUseGuestLogin) {
      setTermsModalOpen(false);
      return;
    }

    setTermsModalLoading(true);
    setTermsModalOpen(false);
    try {
      await onGuestSubmit();
    } finally {
      setTermsModalLoading(false);
    }
  }, [canUseGuestLogin, navigate, onGuestSubmit, termsModalLoading, termsModalMode]);

  useEffect(() => {
    autoGuestTriggeredRef.current = false;
  }, [location.search]);

  useEffect(() => {
    if (!shouldAutoGuest) return;
    if (!canUseGuestLogin) return;
    if (loading || guestLoading || termsModalOpen) return;
    if (autoGuestTriggeredRef.current) return;
    autoGuestTriggeredRef.current = true;
    openTermsModal("guest");
  }, [canUseGuestLogin, guestLoading, loading, openTermsModal, shouldAutoGuest, termsModalOpen]);

  return (
    <main className="xima-email-auth xima-mobile-scroll-main">
      <header className="xima-email-auth-header">
        <Link className="xima-email-auth-logo-link" to="/" aria-label="XIMA, inicio">
          <img
            src="/mejor-logo-336.png"
            alt={i18nT("common.ximaLogo")}
            className="xima-email-auth-logo"
          />
        </Link>
        <Link
          to="/register"
          className="xima-email-auth-register-link"
          onClick={(event) => {
            event.preventDefault();
            openTermsModal("register");
          }}
        >
          {t("register")}
        </Link>
      </header>

      <section className={`xima-email-auth-shell${emailFormOpen ? " is-form-open" : ""}`}>
        <h1>{t("title")}</h1>

        <div className="xima-email-auth-board" aria-hidden="true">
          <span className="xima-email-auth-board-shadow" />
          <img src="/assets/images/xima-landing-board-transparent.png" alt="" />
        </div>

        {verificationContact ? (
          <div className="xima-email-auth-form-panel">
            <EmailVerificationPanel
              contact={verificationContact}
              language={language}
              onVerified={() => setVerificationContact("")}
              onBack={() => setVerificationContact("")}
            />
          </div>
        ) : !emailFormOpen ? (
          <div className="xima-email-auth-entry">
            <button
              type="button"
              className="xima-email-auth-continue"
              onClick={() => setEmailFormOpen(true)}
            >
              <MdEmail aria-hidden="true" />
              <span>{t("continueWithEmail")}</span>
            </button>
            <p>{t("emailOnlyDescription")}</p>
          </div>
        ) : (
        <form className="xima-email-auth-form" onSubmit={onSubmit} noValidate>
          <button
            type="button"
            className="xima-email-auth-back"
            onClick={() => {
              setEmailFormOpen(false);
              setErrors({});
            }}
          >
            <MdArrowBack aria-hidden="true" />
            <span>{t("back")}</span>
          </button>

          <div className="xima-email-auth-field">
            <label htmlFor="xima-login-identifier">{t("username")}</label>
            <div className={errors.username || errors.identifier ? "has-error" : ""}>
              <MdEmail aria-hidden="true" />
              <input
                type="email"
                id="xima-login-identifier"
                name="identifier"
                inputMode="email"
                autoComplete="email"
                required
                placeholder={t("usernamePlaceholder")}
              />
            </div>
            {(errors.username || errors.identifier) && (
              <p className="xima-email-auth-error">
                {errors.username || errors.identifier}
              </p>
            )}
          </div>

          <div className="xima-email-auth-field">
            <label htmlFor="xima-login-password">{t("password")}</label>
            <div className={errors.password ? "has-error" : ""}>
              <MdLock aria-hidden="true" />
              <input
                type="password"
                id="xima-login-password"
                name="password"
                autoComplete="current-password"
                required
                placeholder={t("passwordPlaceholder")}
              />
            </div>
            {errors.password && <p className="xima-email-auth-error">{errors.password}</p>}
          </div>

          <label className="xima-email-auth-remember">
            <input
              type="checkbox"
              checked={rememberSession}
              onChange={(event) => setRememberSession(event.target.checked)}
            />
            <span>{t("rememberSession")}</span>
          </label>

          {errors.form && <p className="xima-email-auth-error is-form">{errors.form}</p>}

          <button
            type="submit"
            className="xima-email-auth-submit"
            disabled={loading || guestLoading}
          >
            {loading ? t("loading") : t("submit")}
          </button>

          {canUseGuestLogin && (
            <>
              <button
                type="button"
                className="xima-email-auth-guest"
                onClick={() => openTermsModal("guest")}
                disabled={loading || guestLoading}
              >
                {guestLoading ? t("guestLoading") : t("guestSubmit")}
              </button>
              <p className="xima-email-auth-guest-terms">
                {t("termsGuestInline")}
              </p>
            </>
          )}

          <div className="xima-email-auth-no-account">
            <p>
              {t("noAccount")}{" "}
              <Link
                to="/register"
                onClick={(event) => {
                  event.preventDefault();
                  openTermsModal("register");
                }}
              >
                {t("register")}
              </Link>
            </p>
          </div>
        </form>
        )}

        <LanguageSelector
          language={language}
          onChange={setLanguage}
          compact
          showDescription={false}
          className="xima-email-auth-language"
        />

        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            description={notification.description}
            onClose={() => setNotification(null)}
          />
        )}
        <TermsAcceptanceModal
          open={termsModalOpen}
          mode={termsModalMode}
          loading={termsModalLoading || guestLoading}
          language={language}
          onAccept={onTermsAccept}
          onCancel={closeTermsModal}
        />
      </section>
    </main>
  );
};

export default LoginForm;
